import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  computed,
  forwardRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { ZendraLocalizationPipe } from '../pipes/zendra-localization.pipe';
import type { ZnSelectOption } from './zn-select.types';

type ZnSimpleSelectInputOption =
  | string
  | { key: string | number; value: string };

type ZnSimpleSelectNormalizedOption = ZnSelectOption & {
  rawKey: string | number;
};

type ZnSimpleSelectOuput = string | number | null;

@Component({
  selector: 'zn-simple-select',
  standalone: true,
  imports: [CommonModule, ZendraLocalizationPipe],
  templateUrl: './zn-simple-select.html',
  styleUrls: ['./zn-select.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ZnSimpleSelect),
      multi: true,
    },
  ],
})
export class ZnSimpleSelect implements ControlValueAccessor {
  options = input<ZnSimpleSelectInputOption[]>([]);
  placeholder = input<string>('SelectOption');
  showPlaceholder = input<boolean>(true);
  showClearSelection = input<boolean>(true);
  localizationResource = input<ZendraLocalizationServices>(
    ZendraLocalizationServices.LanguageService,
  );

  
  @Input() openUp = false;

  selectedChange = output<ZnSimpleSelectOuput>();

  readonly normalizedOptions = computed<ZnSimpleSelectNormalizedOption[]>(() =>
    this.options().map(option => {
      if (typeof option === 'string') {
        return { key: option, value: option, rawKey: option };
      }

      return {
        key: String(option.key),
        value: String(option.value),
        rawKey: option.key,
      };
    }),
  );

  readonly selectedOption = computed(() =>
    this.normalizedOptions().find(option => option.key === String(this.value)) ?? null,
  );

  readonly hasOptions = computed(() => this.normalizedOptions().length > 0);
  readonly hasSelectedValue = computed(() => this.value !== null);

  readonly displayLabel = computed(() => {
    const selected = this.selectedOption();
    if (selected) {
      return selected.value;
    }

    return this.showPlaceholder() ? this.placeholder() : '';
  });

  readonly activeIndex = signal(-1);
  readonly dropdownOpen = signal(false);
  private readonly selectedValue = signal<ZnSimpleSelectOuput>(null);

  disabled = false;

  private readonly elementRef = inject(ElementRef<HTMLElement>);

  private onChange: (value: ZnSimpleSelectOuput) => void = () => {};
  private onTouched: () => void = () => {};

  get value(): ZnSimpleSelectOuput {
    return this.selectedValue();
  }

  writeValue(value: ZnSimpleSelectOuput): void {
    this.selectedValue.set(
      value === null || value === undefined || value === '' ? null : String(value),
    );
    this.syncActiveIndexWithValue();
  }

  registerOnChange(fn: (value: ZnSimpleSelectOuput) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDropdown(): void {
    if (this.disabled) {
      return;
    }

    if (this.dropdownOpen()) {
      this.closeDropdown();
      return;
    }

    this.openDropdown();
  }

  openDropdown(): void {
    if (this.disabled || this.dropdownOpen()) {
      return;
    }

    this.dropdownOpen.set(true);
    this.syncActiveIndexWithValue();
  }

  closeDropdown(): void {
    if (!this.dropdownOpen()) {
      return;
    }

    this.dropdownOpen.set(false);
    this.activeIndex.set(-1);
    this.onTouched();
  }

  selectOption(optionKey: string): void {
    if (this.disabled) {
      return;
    }

    const option = this.normalizedOptions().find(item => item.key === optionKey);
    const value = option?.rawKey ?? optionKey;

    this.selectedValue.set(value);
    this.syncActiveIndexWithValue();
    this.onChange(value);
    this.selectedChange.emit(value);
    this.closeDropdown();
  }

  clearSelection(event?: Event): void {
    event?.stopPropagation();

    if (this.disabled || !this.hasSelectedValue()) {
      return;
    }

    this.selectedValue.set(null);
    this.syncActiveIndexWithValue();
    this.onChange(null);
    this.selectedChange.emit(null);
    this.closeDropdown();
  }

  handleTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.dropdownOpen()) {
        this.openDropdown();
        return;
      }

      this.moveActiveIndex(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.dropdownOpen()) {
        this.openDropdown();
        return;
      }

      this.moveActiveIndex(-1);
    }
  }

  handleMenuKeydown(event: KeyboardEvent): void {
    if (!this.dropdownOpen()) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveActiveIndex(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveActiveIndex(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const option = this.normalizedOptions()[this.activeIndex()];
      if (option) {
        this.selectOption(option.key);
      }
    }
  }

  isSelected(key: string): boolean {
    return String(this.value) === key;
  }

  toLocalizationKey(value: string): string {
    if (!value) {
      return '';
    }

    return value.includes('::')
      ? value
      : `${this.localizationResource()}::${value}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.dropdownOpen()) {
      return;
    }

    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.closeDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeDropdown();
  }

  private moveActiveIndex(direction: 1 | -1): void {
    const options = this.normalizedOptions();
    if (options.length === 0) {
      this.activeIndex.set(-1);
      return;
    }

    const current = this.activeIndex();
    if (current === -1) {
      this.activeIndex.set(direction === 1 ? 0 : options.length - 1);
      return;
    }

    const next = Math.max(0, Math.min(options.length - 1, current + direction));
    this.activeIndex.set(next);
  }

  private syncActiveIndexWithValue(): void {
    const currentValue = this.value;
    const index = this.normalizedOptions().findIndex(
      option => option.key === String(currentValue),
    );
    this.activeIndex.set(index);
  }
}
